// Edit banking (owner/director only — the section itself is only rendered
// when the server sent a `banking` key, which managers never get).
//
// The typed numbers live in this component's state only: never in the store,
// localStorage or the URL, and they are cleared on save and on cancel. The
// server answers with MASKS, which is all the page ever shows afterwards.
import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { authedWrite, ApiError } from '../lib/apiClient';

export interface BankingMasks { bank_routing: string | null; bank_account: string | null }

const strip = (v: string): string => v.replace(/[\s-]/g, '');

function abaOk(r: string): boolean {
  const d = r.split('').map(Number);
  return (3 * (d[0] + d[3] + d[6]) + 7 * (d[1] + d[4] + d[7]) + (d[2] + d[5] + d[8])) % 10 === 0;
}

export function validateBanking(routingRaw: string, accountRaw: string): { routing?: string; account?: string; form?: string } {
  const routing = strip(routingRaw);
  const account = strip(accountRaw);
  const errors: { routing?: string; account?: string; form?: string } = {};
  if (!routing && !account) errors.form = 'Enter a routing number, an account number, or both.';
  if (routing) {
    if (!/^[0-9]{9}$/.test(routing)) errors.routing = 'Routing number must be exactly 9 digits.';
    else if (!abaOk(routing)) errors.routing = 'That routing number fails the bank check digit. Re-check it.';
  }
  if (account && !/^[0-9]{4,17}$/.test(account)) errors.account = 'Account number must be 4 to 17 digits.';
  return errors;
}

export function CompanyBankingEdit({ companyCode, onSaved }: {
  companyCode: string;
  onSaved: (masks: BankingMasks) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [routing, setRouting] = useState('');
  const [account, setAccount] = useState('');
  const [errors, setErrors] = useState<{ routing?: string; account?: string; form?: string }>({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  function close(): void {
    setRouting('');
    setAccount('');
    setErrors({});
    setOpen(false);
  }

  async function save(e: Event): Promise<void> {
    e.preventDefault();
    const found = validateBanking(routing, account);
    setErrors(found);
    if (found.routing || found.account || found.form) return;
    setBusy(true);
    try {
      const payload: { bankRouting?: string; bankAccount?: string } = {};
      if (strip(routing)) payload.bankRouting = strip(routing);
      if (strip(account)) payload.bankAccount = strip(account);
      const body = await authedWrite<{ banking: BankingMasks }>(
        'PUT', `/api/v1/company/${companyCode}/banking`, payload,
      );
      onSaved(body.banking);
      setSaved(true);
      close();
    } catch (err) {
      setErrors({ form: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <>
        <button type="button" class="btn" data-testid="banking-edit-open"
          onClick={() => { setSaved(false); setOpen(true); }}>Edit banking</button>
        {saved ? <small class="muted" role="status"> Banking saved.</small> : null}
      </>
    );
  }
  return (
    <form data-testid="banking-edit-form" autoComplete="off" onSubmit={(e) => void save(e)}>
      <label>Routing number
        <input type="password" name="bank-routing-entry" autoComplete="off" inputMode="numeric"
          data-testid="banking-routing-input" value={routing}
          onInput={(e) => setRouting((e.target as HTMLInputElement).value)} />
      </label>
      {errors.routing ? <small class="bad" role="alert">{errors.routing}</small> : null}
      <label>Account number
        <input type="password" name="bank-account-entry" autoComplete="off" inputMode="numeric"
          data-testid="banking-account-input" value={account}
          onInput={(e) => setAccount((e.target as HTMLInputElement).value)} />
      </label>
      {errors.account ? <small class="bad" role="alert">{errors.account}</small> : null}
      {errors.form ? <small class="bad" role="alert">{errors.form}</small> : null}
      <small class="muted">Leave a box empty to keep what is on file.</small>
      <div>
        <button type="submit" class="btn" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" class="btn" disabled={busy} onClick={close}>Cancel</button>
      </div>
    </form>
  );
}
